import React, { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { IoDocumentTextOutline, IoCloseOutline, IoChevronBack, IoChevronForward } from 'react-icons/io5';

const PDFViewer = ({ documents, exudateLevel, productType }) => {
  const [selectedPdf, setSelectedPdf] = useState(null);
  const [currentIndex, setCurrentIndex] = useState(0);

  if (!documents || documents.length === 0) {
    return null;
  }

  const openPdf = (doc, index) => {
    setSelectedPdf(doc);
    setCurrentIndex(index);
  };

  const closePdf = () => {
    setSelectedPdf(null);
  };

  const nextPdf = () => {
    const nextIndex = (currentIndex + 1) % documents.length;
    setSelectedPdf(documents[nextIndex]);
    setCurrentIndex(nextIndex);
  };

  const prevPdf = () => {
    const prevIndex = (currentIndex - 1 + documents.length) % documents.length;
    setSelectedPdf(documents[prevIndex]);
    setCurrentIndex(prevIndex);
  };

  return (
    <>
      <motion.div
        className="mb-8"
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
      >
        <div className="bg-gradient-to-r from-blue-50 to-cyan-50 dark:from-blue-900/20 dark:to-cyan-900/20 border-2 border-blue-200 dark:border-blue-800 rounded-2xl p-6">
          <h3 className="text-lg font-bold text-gray-900 dark:text-white mb-4 flex items-center gap-2">
            <IoDocumentTextOutline className="text-blue-600 dark:text-blue-400 text-2xl" />
            Reference Documents
            <span className="text-sm font-normal text-gray-600 dark:text-gray-400">
              ({exudateLevel} exudate, {productType === 'collagen_powder_1g' ? 'Collagen Powder' : 'Collagen Sheet'})
            </span>
          </h3>
          <p className="text-sm text-gray-700 dark:text-gray-300 mb-4">
            Click on any document below to view it. These documents provide additional information about the selected product.
          </p>
          
          <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
            {documents.map((doc, index) => (
              <motion.button
                key={index}
                onClick={() => openPdf(doc, index)}
                className="p-4 bg-white dark:bg-gray-800 border-2 border-gray-200 dark:border-gray-700 rounded-xl hover:border-blue-500 dark:hover:border-blue-400 transition-all group"
                whileHover={{ scale: 1.03, y: -2 }}
                whileTap={{ scale: 0.98 }}
              >
                <div className="flex flex-col items-center gap-2">
                  <IoDocumentTextOutline className="text-4xl text-blue-600 dark:text-blue-400 group-hover:scale-110 transition-transform" />
                  <span className="text-sm font-semibold text-gray-900 dark:text-white text-center">
                    {doc.name}
                  </span>
                </div>
              </motion.button>
            ))}
          </div>
        </div>
      </motion.div>

      {/* PDF Modal Viewer */}
      <AnimatePresence>
        {selectedPdf && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 z-50 flex items-center justify-center bg-black/80 backdrop-blur-sm p-4"
            onClick={closePdf}
          >
            <motion.div
              initial={{ scale: 0.9, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              exit={{ scale: 0.9, opacity: 0 }}
              onClick={(e) => e.stopPropagation()}
              className="relative w-full h-full max-w-6xl max-h-[90vh] bg-white dark:bg-gray-900 rounded-2xl shadow-2xl overflow-hidden flex flex-col"
            >
              {/* Header */}
              <div className="flex items-center justify-between p-4 border-b border-gray-200 dark:border-gray-700 bg-gray-50 dark:bg-gray-800">
                <div className="flex items-center gap-4">
                  <button
                    onClick={prevPdf}
                    className="p-2 hover:bg-gray-200 dark:hover:bg-gray-700 rounded-lg transition-colors"
                    title="Previous document"
                  >
                    <IoChevronBack className="text-xl text-gray-700 dark:text-gray-300" />
                  </button>
                  <h3 className="text-lg font-bold text-gray-900 dark:text-white">
                    {selectedPdf.name}
                  </h3>
                  <span className="text-sm text-gray-600 dark:text-gray-400">
                    ({currentIndex + 1} of {documents.length})
                  </span>
                  <button
                    onClick={nextPdf}
                    className="p-2 hover:bg-gray-200 dark:hover:bg-gray-700 rounded-lg transition-colors"
                    title="Next document"
                  >
                    <IoChevronForward className="text-xl text-gray-700 dark:text-gray-300" />
                  </button>
                </div>
                <button
                  onClick={closePdf}
                  className="p-2 hover:bg-gray-200 dark:hover:bg-gray-700 rounded-lg transition-colors"
                >
                  <IoCloseOutline className="text-2xl text-gray-700 dark:text-gray-300" />
                </button>
              </div>

              {/* PDF Viewer */}
              <div className="flex-1 overflow-hidden">
                <iframe
                  src={`${selectedPdf.url}#toolbar=0&navpanes=0&scrollbar=0`}
                  className="w-full h-full"
                  title={selectedPdf.name}
                  style={{ border: 'none' }}
                />
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </>
  );
};

export default PDFViewer;